/* ── Vehicle fitment flagging on collection & search grids ──────────────────
   Reads the same localStorage garage vehicle-fitment.js manages, via the
   shared matching logic in vehicle-fitment-matching.js (must load first —
   see layout/theme.liquid). Flags any product card whose tags (or,
   opportunistically, a `custom.fitment` metafield if the merchant has set
   one up) match the active vehicle, then sorts matched cards to the front
   of their grid. Non-matching products are never hidden by default — only
   the explicit "Fits My Vehicle Only" toggle hides them.
   ───────────────────────────────────────────────────────────────────────── */
(() => {
  const FITMENT_FILTER_KEY = 'circuit:fitment-filter-active';
  const GRID_SELECTOR = '.grid--cards';
  const CARD_SELECTOR = '.product-card[data-product-tags]';

  const isFitmentFilterActive = () => {
    try {
      return localStorage.getItem(FITMENT_FILTER_KEY) === 'true';
    } catch (_) {
      return false;
    }
  };

  const setFitmentFilterActive = (active) => {
    try {
      localStorage.setItem(FITMENT_FILTER_KEY, active ? 'true' : 'false');
    } catch (_) {
      /* no-op */
    }
  };

  // Stable-sorts matched cards to the front while preserving the merchant's
  // original ordering within each group — so clearing the vehicle (every
  // card becomes "unmatched") naturally restores the original layout.
  const sortGrid = (grid) => {
    const children = Array.from(grid.children);
    children.forEach((child, i) => {
      if (child.dataset.pfOriginalIndex === undefined) child.dataset.pfOriginalIndex = String(i);
    });
    children
      .slice()
      .sort((a, b) => {
        const aMatched = a.classList.contains('is-vehicle-fit') ? 0 : 1;
        const bMatched = b.classList.contains('is-vehicle-fit') ? 0 : 1;
        if (aMatched !== bMatched) return aMatched - bMatched;
        return Number(a.dataset.pfOriginalIndex) - Number(b.dataset.pfOriginalIndex);
      })
      .forEach((child) => grid.appendChild(child));
  };

  const applyFitment = () => {
    const { getAllVehicles, getActiveVehicle, buildCandidateTags, matchesCard, buildLabel } = window.LakiFitment;
    const activeVehicle = getActiveVehicle();
    const allVehicles = getAllVehicles();
    const activeCandidates = buildCandidateTags(activeVehicle);
    // Hard filter uses all-garage scope so "Fits My Vehicle Only" respects every saved car.
    const anyGarageCandidates = allVehicles.some((v) => {
      const c = buildCandidateTags(v);
      return c.fuzzy.length > 0 || c.exact.length > 0;
    });
    const hardFilter = anyGarageCandidates && isFitmentFilterActive();

    if (allVehicles.length) console.debug('[LakiFitment] garage:', allVehicles.map(buildLabel), '| active:', activeVehicle ? buildLabel(activeVehicle) : 'none');

    document.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
      grid.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        // Active vehicle takes label priority; fall back to first matching garage vehicle.
        let matched = false;
        let vehicleLabel = '';

        if (activeVehicle && matchesCard(card, activeCandidates)) {
          matched = true;
          vehicleLabel = buildLabel(activeVehicle).toUpperCase();
        } else {
          for (const v of allVehicles) {
            if (activeVehicle && v.id === activeVehicle.id) continue;
            if (matchesCard(card, buildCandidateTags(v))) {
              matched = true;
              vehicleLabel = buildLabel(v).toUpperCase();
              break;
            }
          }
        }

        card.classList.toggle('is-vehicle-fit', matched);

        const chip = card.querySelector('[data-vehicle-fitment-chip]');
        if (chip) chip.hidden = !matched;

        const imgBadge = card.querySelector('[data-fitment-img-badge]');
        if (imgBadge) {
          if (matched) {
            const vehicleSpan = imgBadge.querySelector('[data-fitment-badge-vehicle]');
            if (vehicleSpan) vehicleSpan.textContent = vehicleLabel;
            imgBadge.hidden = false;
          } else {
            imgBadge.hidden = true;
          }
        }

        card.hidden = hardFilter && !matched;
      });
      sortGrid(grid);
    });
  };

  /* ── "Fitment" section in the filter panel — vehicle card + hide toggle ──
     #facets-panel's innerHTML is fully replaced on every AJAX filter swap,
     so this has to re-query and re-bind every time, not just once on load. */
  const buildEngineLabel = (vehicle) => (vehicle && vehicle.engine ? vehicle.engine : '');

  const renderFitmentPanel = () => {
    const section = document.querySelector('[data-fitment-section]');
    if (!section) return;

    const { getActiveVehicle, buildLabel } = window.LakiFitment;
    const vehicle = getActiveVehicle();
    const emptyState = section.querySelector('[data-fitment-empty]');
    const vehicleState = section.querySelector('[data-fitment-vehicle]');
    const label = section.querySelector('[data-fitment-label]');
    const engine = section.querySelector('[data-fitment-engine]');
    const toggle = section.querySelector('[data-fitment-filter-toggle]');

    if (vehicle) {
      if (emptyState) emptyState.hidden = true;
      if (vehicleState) vehicleState.hidden = false;
      if (label) label.textContent = buildLabel(vehicle);
      const engineLabel = buildEngineLabel(vehicle);
      if (engine) {
        engine.textContent = engineLabel;
        engine.hidden = !engineLabel;
      }
    } else {
      if (emptyState) emptyState.hidden = false;
      if (vehicleState) vehicleState.hidden = true;
      setFitmentFilterActive(false);
    }

    if (toggle && toggle.dataset.fitmentToggleBound !== 'true') {
      toggle.dataset.fitmentToggleBound = 'true';
      toggle.addEventListener('change', () => {
        setFitmentFilterActive(toggle.checked);
        applyFitment();
      });
    }
    if (toggle) toggle.checked = isFitmentFilterActive();
  };

  const init = () => {
    applyFitment();
    renderFitmentPanel();
  };

  init();
  window.addEventListener('vehicle:selected', init);
  window.addEventListener('vehicle:garage-changed', init);
  // Fired after the AJAX facet-filter swap replaces #facets-panel/#facets-results (theme.js).
  document.addEventListener('laki:facets:updated', init);
})();
